<template>
    <div class="tournament-profile-card">
        <!-- Desktop Header -->
        <div class="header d-none d-sm-flex" v-if="participant">
            <template v-if="showDisqualificationTooltip">
                <DisqualificationTooltip :participant-id="participant.participantId" position="bottom">
                    <span :class="'participant-status ' + getStatusColor()">{{ $t(capitalizedParticipantStatus) }}<i
                            :class="getStatusIcon()"></i></span>
                </DisqualificationTooltip>
            </template>
            <template v-else>
                    <span :class="'participant-status ' + getStatusColor()">
                        {{ $t(capitalizedParticipantStatus) }}
                        <i :class="getStatusIcon()"></i>
                    </span>
            </template>
            <Flag class="flag" :code="countryCode" size="S"></Flag>
        </div>

        <!-- Mobile Header - Enhanced layout -->
        <div class="mobile-header d-flex d-sm-none" v-if="participant">
            <template v-if="showDisqualificationTooltip">
                <DisqualificationTooltip :participant-id="participant.participantId" position="bottom">
                    <span :class="'mobile-status ' + getStatusColor()">
                        <i :class="getStatusIcon() + ' mr-1'"></i>
                        {{ $t(capitalizedParticipantStatus) }}
                    </span>
                </DisqualificationTooltip>
            </template>
            <template v-else>
                <span :class="'mobile-status ' + getStatusColor()">
                    <i :class="getStatusIcon() + ' mr-1'"></i>
                    {{ $t(capitalizedParticipantStatus) }}
                </span>
            </template>
            <Flag class="mobile-flag" :code="countryCode" size="S"></Flag>
        </div>

        <!-- Desktop Body -->
        <div class="body d-none d-sm-block" v-if="participant">
            <v-avatar class="avatar-profile justify-center" size="105">
                <v-img :src="!isPublic ? avatar.imageURL : publicParticipant.profilePicture"></v-img>
            </v-avatar>
            <p class="name-txt">{{ participant?.name || (isPublic ? (publicParticipant.firstName + " " +
                publicParticipant.lastName) : null) || (this.$store.state.user.firstName + " " +
                    this.$store.state.user.lastName) }}</p>
            <p v-if="participant"><i class="fa-regular fa-wallet mr-3"></i><b>{{ $t('joinedWith') }}:</b> {{
                brokerAccount?.identifier }}<i v-if="canChangeAcc" class="fa-regular fa-shuffle ml-3 change-acc-btn"
                    @click="changeAccountCallback()"></i></p>
            <div class="participant-info">
                <p class="ratio"><b>{{ rankingHeader }}: {{ formattedScore }}</b></p>
                <p class="rank">{{ rank }}° {{ $t('atRank') }}</p>
            </div>
        </div>

        <!-- Mobile Body - Compact layout -->
        <div class="mobile-body d-block d-sm-none" v-if="participant">
            <div class="mobile-user-info">
                <v-avatar class="mobile-avatar" size="64">
                    <v-img :src="!isPublic ? avatar.imageURL : publicParticipant.profilePicture"></v-img>
                </v-avatar>
                <div class="mobile-user-details">
                    <p class="mobile-name">{{ participant?.name || (isPublic ? (publicParticipant.firstName + " " +
                        publicParticipant.lastName) : null) || (this.$store.state.user.firstName + " " +
                            this.$store.state.user.lastName) }}</p>
                    <p class="mobile-broker"><i class="fa-regular fa-wallet mr-1"></i>{{ brokerAccount?.identifier }}
                        <i v-if="canChangeAcc" class="fa-regular fa-shuffle ml-2 mobile-change-btn"
                            @click="changeAccountCallback()"></i>
                    </p>
                </div>
            </div>

            <div class="mobile-stats">
                <div class="mobile-stat-item">
                    <span class="stat-label">{{ rankingHeader }}</span>
                    <span class="stat-value">{{ formattedScore }}</span>
                </div>
                <div class="mobile-stat-item">
                    <span class="stat-label">{{ $t('rank') }}</span>
                    <span class="stat-value">{{ rank }}°</span>
                </div>
            </div>
        </div>

        <!-- Bracket Progress (Desktop) -->
        <BracketProgress
            v-if="showBracketProgress && bracketTournamentId"
            class="d-none d-sm-block"
            :score-bracket="participant.scoreBracket"
            :tournament-id="bracketTournamentId"
        />

        <!-- Desktop Footer -->
        <div class="footer d-none d-sm-flex" v-if="participant">
            <button v-if="!isPublic" class="share-btn" @click="toggleShareLink"><i
                    class="fa-regular fa-share-nodes mr-3"></i>{{
                        $t('shareMyProgress') }}</button>
        </div>

        <!-- Bracket Progress (Mobile) -->
        <BracketProgress
            v-if="showBracketProgress && bracketTournamentId"
            class="d-block d-sm-none"
            :score-bracket="participant.scoreBracket"
            :tournament-id="bracketTournamentId"
        />

        <!-- Mobile Footer - Compact layout -->
        <div class="mobile-footer d-flex d-sm-none" v-if="participant && !isPublic">
            <button class="mobile-share-btn" @click="toggleShareLink">
                <i class="fa-regular fa-share-nodes mr-2"></i>{{ $t('share') }}
            </button>
        </div>

        <p class="not-enrolled-txt" v-if="!participant">{{ $t('notParticipating') }}</p>
        <ShareLink :tournamentParticipant="participant" v-if="showShareLink" :fundingChallengeTestId="1"
            @closeWindow="hideShareModal" />
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import ShareLink from "@/views/Funding/Simulation/components/ShareLinkTest.vue";
import DisqualificationTooltip from "@/components/DisqualificationTooltip.vue";
import BracketProgress from "@/components/BracketProgress.vue";

export default defineComponent({
    name: 'TournamentProfileCard',
    components: {
        ShareLink,
        DisqualificationTooltip,
        BracketProgress,
    },
    props: {
        tournament: {
            type: Object,
            required: false
        },
        participant: {
            type: Object,
            required: true,
            default: { status: '' }
        },
        countryCode: {
            type: String,
            required: true,
            default: 'CO'
        },
        brokerAccount: {
            type: Object,
            required: true,
            default: { identifier: 'Swiset Wallet' }
        },
        swisetRatio: {
            type: Number,
            required: false,
            default: 0
        },
        rank: {
            type: Number || String,
            required: true,
            default: 100
        },
        shareBtnCallback: {
            type: Function,
            required: false,
        },
        changeAccountCallback: {
            type: Function,
            required: false
        },
        canChangeAcc: {
            type: Boolean,
            required: false,
            default: false
        },
        rankingType: {
            type: String,
            required: false,
        }
    },
    computed: {
        showDisqualificationTooltip() {
            return this.participant?.status === 'DISQUALIFIED';
        },
        capitalizedParticipantStatus() {
            return this.getParticipantStatus().text;
        },
        rankingHeader() {
            // Get the ranking type from props or fallback to store
            const rankingType = this.rankingType || this.$store.state.tournament?.tournamentSettings.tournamentRankingType;
            alert

            // Return the appropriate header based on the ranking type - same logic as Table.vue
            switch (rankingType) {
                case 'SWISET_RATIO':
                    return this.$t('swisetRatio');
                case 'BALANCE':
                    return this.$t('balance');
                case 'EQUITY_RT':
                    return this.$t('equity');
                case 'PROFIT_PERCENTAGE':
                    return this.$t('profit');
                case 'GROSS_LOSS':
                    return this.$t('grossLoss');
                case 'PERFORMANCE_RATIO':
                    return this.$t('performanceRatio');
                default:
                    return this.$t('profit');
            }
        },
        showBracketProgress() {
            const features = this.$store.state.tournament?.selectedFeatures;
            return features && Array.isArray(features) && features.includes('TOURNAMENT_RANKING_AGG_VOLUME')
                && this.participant?.scoreBracket != null;
        },
        bracketTournamentId() {
            return this.$store.state.tournament?.tournamentSettings?.tournament?.id || null;
        },
        formattedScore() {
            const value = this.swisetRatio;
            if (value === null || value === undefined) return '-';

            // Get the ranking type from props or fallback to store
            const rankingType = this.rankingType || this.$store.state.tournament?.tournamentSettings.tournamentRankingType || 'SWISET_RATIO';

            // Format the value based on the ranking type - similar to formatRankingValue in Table.vue
            switch (rankingType) {
                case 'BALANCE':
                    // Format with thousands separator
                    return new Intl.NumberFormat(this.$i18n.locale).format(value.toFixed(2));

                case 'PROFIT_PERCENTAGE':
                case 'PERFORMANCE_RATIO':
                    // Format as percentage
                    return `${value.toFixed(2)}%`;

                case 'SWISET_RATIO':
                case 'EQUITY_RT':
                case 'GROSS_LOSS':
                default:
                    // Format as plain number
                    return value.toFixed(2);
            }
        }
    },
    data() {
        return {
            avatar: {
                imageURL: this.$store.state.user.imageUrl
            },
            showShareLink: false,
            publicParticipant: {},
            isPublic: false,
        }
    },
    created() {
        this.setParticipantEmbedRank();
    },
    methods: {
        toggleShareLink() {
            this.showShareLink = !this.showShareLink;
        },
        showShareModal() {
            this.showShareLink = true;
        },
        hideShareModal() {
            this.showShareLink = false;
        },
        getParticipantStatus() {
            const status = this.participant?.status?.toLowerCase() || '';
            if (status === 'winner' || (status === 'active' && this.rank > 0 && this.rank <= 3 && (new Date() > new Date(this.$store.state.tournament.endTime)))) {
                return { text: 'winner', color: 'winning', icon: 'fa-regular fa-medal' };
            }
            if (status === 'active' && this.rank > 0 && this.rank <= 1) {
                return { text: 'winning', color: 'winning', icon: 'fa-regular fa-medal' };
            }
            if (status === 'active' || status === 'participating') {
                return { text: 'participating', color: 'active', icon: 'fa-regular fa-gamepad' };
            }
            if (status === 'paused') {
                return { text: 'notConsistent', color: 'paused', icon: 'fa-regular fa-triangle-exclamation' };
            }
            if (status === 'disqualified') {
                return { text: 'disqualified', color: 'disqualified', icon: 'fa-regular fa-circle-exclamation' };
            }
            return { text: status.charAt(0).toUpperCase() + status.slice(1), color: 'no-enrolled', icon: '' };
        },
        setParticipantEmbedRank() {
            if (this.$route.query.identifier) {
                let acc = this.$route.query.identifier;
                let participant = this.$store.state.tournament.participants.find(p => p.brokerAccountIdentifier === acc);
                this.publicParticipant = participant;
                this.isPublic = true;
            }
        },
        getStatusColor() {
            return this.getParticipantStatus().color;
        },
        getStatusIcon() {
            return this.getParticipantStatus().icon;
        }
    },
});
</script>

<style lang="scss" scoped>
$swt-success: var(--v-mainGreen-base);
$swt-error: #C42A14;
$swt-yellow: #FFF75F;
$swt-warning: #FF8933;

.tournament-profile-card {
    border: 1px solid var(--v-mediumGray-base);
    border-radius: 15px;
    margin-left: auto;
    background-color: var(--v-fullWhite-base);
    font-family: 'Avenir', sans-serif;
    text-align: center;
    position: relative;
    overflow: hidden;

    .header {
        display: flex;
        justify-content: space-between;
        padding-top: 30px;
        align-items: center;
        margin-bottom: 24px;

        .participant-status {
            border-top-right-radius: 20px;
            border-bottom-right-radius: 20px;
            background-color: var(--v-mainBlack-base);
            color: #fff;
            padding: 10px 20px;

            &.active {
                background-color: #9a9a9a;
            }

            &.winning {
                background-color: $swt-success;
            }

            &.paused {
                background-color: $swt-warning;
            }

            &.disqualified {
                background-color: $swt-error;
            }

            &.no-enrolled {
                background-color: var(--v-black_lightGrey-base);
            }

            i {
                margin-left: 10px;
            }
        }

        .flag {
            margin-right: 24px;
            height: 20px;
            width: 25px;
        }
    }

    .body {
        .avatar-profile {
            border: 2px solid black;
            margin-bottom: 12px;
        }

        .name-txt {
            font-size: 20px;
            font-weight: 700;
            margin-bottom: 5px;
        }

        .participant-info {
            display: flex;
            justify-content: space-around;
            margin-bottom: 10px;
            border: 1px solid #9A9A9A;
            border-radius: 10px;
            margin: 12px 24px;
            padding: 10px 0;

            .ratio {
                font-size: 14px;
                font-weight: 700;
            }

            .rank {
                font-size: 14px;
                font-weight: 700;
            }
        }

        .change-acc-btn {
            font-size: 16px;
            color: var(--v-mainGreen-base);
            cursor: pointer;
            border-radius: 50%;
            border: 1px solid var(--v-mainGreen-base);
            padding: 5px;
        }
    }

    .footer {
        padding-bottom: 30px;

        .share-btn {
            width: calc(100% - 48px);
            margin: 0 24px;
            color: var(--v-mainGreen-base);
            border: 1px solid var(--v-mainGreen-base);
            border-radius: 30px;
            padding: 8px 20px;
            font-size: 16px;
            cursor: pointer;
            transition: all 0.3s ease;
        }

        &:hover {
            .share-btn {
                background-color: var(--v-mainGreen-base);
                color: var(--v-mainWhite-base);
            }
        }
    }

    .not-enrolled-txt {
        font-size: 22px;
        font-weight: bold;
        color: var(--v-mainBlack-base);
        padding: 20px;
    }

    /* Mobile-specific styles */
    .mobile-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 15px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);

        .mobile-status {
            font-size: 12px;
            font-weight: 500;
            padding: 4px 10px;
            border-radius: 12px;
            display: inline-flex;
            align-items: center;

            &.active {
                background-color: rgba(154, 154, 154, 0.2);
                color: #666;
            }

            &.winning {
                background-color: rgba($swt-success, 0.15);
                color: $swt-success;
            }

            &.paused {
                background-color: rgba($swt-warning, 0.15);
                color: $swt-warning;
            }

            &.disqualified {
                background-color: rgba($swt-error, 0.15);
                color: $swt-error;
            }

            &.no-enrolled {
                background-color: rgba(154, 154, 154, 0.2);
                color: #666;
            }

            i {
                font-size: 11px;
            }
        }

        .mobile-flag {
            height: 16px;
            width: 20px;
        }
    }

    .mobile-body {
        padding: 12px 15px;

        .mobile-user-info {
            display: flex;
            align-items: center;
            text-align: left;
            margin-bottom: 12px;

            .mobile-avatar {
                border: 1px solid #ddd;
                margin-right: 12px;
            }

            .mobile-user-details {
                flex: 1;

                .mobile-name {
                    font-size: 16px;
                    font-weight: 600;
                    margin: 0 0 4px;
                    line-height: 1.3;
                }

                .mobile-broker {
                    font-size: 12px;
                    color: #666;
                    margin: 0;
                    line-height: 1.3;

                    .mobile-change-btn {
                        color: var(--v-mainGreen-base);
                        cursor: pointer;
                        border-radius: 50%;
                        border: 1px solid var(--v-mainGreen-base);
                        padding: 2px;
                        font-size: 12px;
                    }
                }
            }
        }

        .mobile-stats {
            display: flex;
            justify-content: space-between;
            margin: 0 -5px;

            .mobile-stat-item {
                flex: 1;
                background-color: rgba(0, 0, 0, 0.02);
                border-radius: 8px;
                padding: 6px 8px;
                margin: 0 5px;
                display: flex;
                flex-direction: column;

                .stat-label {
                    font-size: 11px;
                    color: #666;
                    margin-bottom: 2px;
                }

                .stat-value {
                    font-size: 16px;
                    font-weight: bold;
                    color: var(--v-mainBlack-base);
                }
            }
        }
    }

    .mobile-footer {
        padding: 0 15px 12px;
        justify-content: center;

        .mobile-share-btn {
            background-color: transparent;
            border: 1px solid var(--v-mainGreen-base);
            color: var(--v-mainGreen-base);
            border-radius: 20px;
            padding: 6px 16px;
            font-size: 14px;
            cursor: pointer;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;

            &:active {
                background-color: rgba(var(--v-mainGreen-base), 0.1);
            }
        }
    }
}

// Additional responsive adjustments
@media (max-width: 600px) {
    .tournament-profile-card {
        border-radius: 12px;
        margin-bottom: 16px;

        .not-enrolled-txt {
            font-size: 16px;
            padding: 16px;
        }
    }
}
</style>

